import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { getAppBrand, getAppTheme } from "../config/resolve-app-brand";
import { applyBrandCssVars } from "../utils/themes/apply-brand-css-vars";
import { applyBrandFavicon } from "../utils/themes/apply-brand-favicon";
import { getCookie, setCrossDomainCookie } from "../utils/cookie-utils";
import themes from "devextreme/ui/themes";

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const storedTheme = getCookie("theme");
  const prefersDarkMode =
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  const [isDarkMode, setIsDarkMode] = useState(
    storedTheme ? storedTheme === "dark" : prefersDarkMode
  );

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => !prev);
  };

  const appBrand = useMemo(() => getAppBrand(), []);
  const appliedTheme = useMemo(() => getAppTheme(isDarkMode), [isDarkMode]);

  useEffect(() => {
    setCrossDomainCookie("theme", isDarkMode ? "dark" : "light");

    document.documentElement.classList.toggle("dark", isDarkMode);
    applyBrandCssVars(appBrand, isDarkMode);
    applyBrandFavicon(appBrand, isDarkMode);

    const themeName = isDarkMode
      ? "material.custom-scheme-dark"
      : "material.custom-scheme-light";
    themes.current(themeName);
    themes.ready(() => {
      console.log("Switched to theme:", themeName);
    });
  }, [appBrand, isDarkMode]);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
      <MuiThemeProvider theme={appliedTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
